/**
 * Moxie Mega Menu — Desktop panel controller + Mobile brands panel
 *
 * PERF (MOXIE): el contenido de cada panel se emite dentro de un <template>
 * inerte (ver sections/mox-mega-menu.liquid) para NO pesar en el DOM inicial de
 * todas las páginas. Aquí se clona al <body> la PRIMERA vez que se interactúa
 * con su nav item (hover/focus desktop, tap del drawer mobile). Reusos
 * posteriores no reconstruyen. En el Theme Editor (request.design_mode) el
 * contenido se renderiza directo (sin <template>) y se adopta en el arranque.
 *
 * Desktop:
 *   .mox-mmenu[data-mox-menu]
 *   Activated by hover/focus on [data-mox-nav] nav <li> items.
 *
 * Mobile (brands layout only):
 *   .mox-mob-panel[data-mox-mobile-panel]   — Level 2: brand list
 *   .mox-mob-chars-panel[data-mox-chars-panel] — Level 3: characters per brand
 *   Activated by tap on Horizon's drawer link #HeaderDrawer-{handle}
 */

import { throttle } from './utilities.js';

const ACTIVE_CLASS      = 'mox-mmenu--active';
const MOB_ACTIVE_CLASS  = 'mox-mob-panel--active';
const HIDE_DELAY        = 120;

class MoxMegaMenu {
  constructor() {
    /** @type {Map<string, HTMLTemplateElement>} handle → <template> (producción) */
    this.templates = new Map();

    /** @type {Set<string>} handles cuyos paneles ya existen en el DOM */
    this.built = new Set();

    /** @type {Map<string, HTMLElement>} handle → inner .mox-mmenu element (desktop) */
    this.panels = new Map();

    /** @type {Map<string, HTMLElement>} handle → .mox-mob-panel element (mobile) */
    this.mobilePanels = new Map();

    this._hideTimer   = null;
    this._activeHandle = null;
    this._headerRectCache = null;

    this._init();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Init
  // ──────────────────────────────────────────────────────────────────────────

  _init() {
    // Producción: indexar los <template> por handle (sin clonar nada aún).
    document.querySelectorAll('[data-mox-menu-tpl]').forEach(tpl => {
      const handle = tpl.dataset.moxMenuTpl;
      if (handle) this.templates.set(handle, tpl);
    });

    // Theme Editor: el contenido se rinde directo → adoptarlo ahora.
    this._adoptRenderedPanels();

    // Disparadores (funcionan igual en ambos modos).
    this._bindDesktopTriggers();
    this._bindMobileTriggers();
    this._bindEditor();

    // PERF (MOXIE): precargar (clonar/enlazar) todos los paneles en idle, para
    // que el primer hover real solo haga _show() (clases + estilos) sin el
    // costo de construcción de DOM. _ensureBuilt() es idempotente, así que si
    // el usuario hace hover antes de que esto corra no se duplica trabajo.
    const idle = window.requestIdleCallback || (cb => setTimeout(cb, 200));
    idle(() => this.templates.forEach((_, handle) => this._ensureBuilt(handle)));

    this._updateTopPosition();

    const onViewportChange = throttle(() => {
      this._invalidateHeaderRect();
      this._updateTopPosition();
    }, 150);
    window.addEventListener('resize', onViewportChange, { passive: true });
    window.addEventListener('scroll', onViewportChange, { passive: true });
  }

  /**
   * Modo Theme Editor: los paneles vienen renderizados dentro de
   * .mox-mega-menu-section (no en <template>). Se teletransportan a <body> (para
   * que position:fixed no lo afecte el sticky header ni infle
   * --header-group-height) y se registran/enlazan como si estuvieran "built".
   */
  _adoptRenderedPanels() {
    document.querySelectorAll('.mox-mega-menu-section').forEach(section => {
      // Saltar secciones que solo llevan <template> (producción).
      if (!section.querySelector('[data-mox-menu]')) return;
      if (section.parentElement !== document.body) document.body.appendChild(section);
    });
    document.querySelectorAll('[data-mox-mobile-panel], [data-mox-chars-panel]').forEach(panel => {
      if (panel.parentElement !== document.body) document.body.appendChild(panel);
    });

    document.querySelectorAll('[data-mox-menu]').forEach(inner => {
      const handle = inner.dataset.moxMenu;
      if (handle && !this.panels.has(handle)) {
        this.panels.set(handle, inner);
        this._bindDesktopPanel(inner, handle);
        this.built.add(handle);
      }
    });
    document.querySelectorAll('[data-mox-mobile-panel]').forEach(panel => {
      const handle = panel.dataset.moxMobilePanel;
      if (handle && !this.mobilePanels.has(handle)) {
        this.mobilePanels.set(handle, panel);
        this._bindMobilePanel(panel, handle);
        this.built.add(handle);
      }
    });
    document.querySelectorAll('[data-mox-chars-panel]').forEach(cp => this._bindCharsPanel(cp));
  }

  /**
   * Construye (una sola vez) el panel de un handle clonando su <template> al
   * <body>. No-op si ya está construido o si no hay template (modo editor).
   */
  _ensureBuilt(handle) {
    if (this.built.has(handle)) return;
    this.built.add(handle); // marcar aunque no haya nada, para no reintentar

    const tpl = this.templates.get(handle);
    if (!tpl) return;

    const frag = tpl.content.cloneNode(true);
    const nodes = Array.from(frag.children).filter(n => n.nodeType === 1);

    nodes.forEach(node => {
      if (node.matches('[data-mox-menu]')) {
        // El panel desktop DEBE vivir dentro de un .mox-mega-menu-section: es ese
        // wrapper el que tiene position:fixed y la visibilidad vía
        // :has(.mox-mmenu--active) en _mox-mega-menu.scss. Lo recreamos en <body>
        // (réplica del teletransporte original de la sección completa).
        const wrapper = document.createElement('div');
        wrapper.className = 'mox-mega-menu-section';
        wrapper.appendChild(node);
        document.body.appendChild(wrapper);
        this.panels.set(handle, node);
        this._bindDesktopPanel(node, handle);
      } else if (node.matches('[data-mox-mobile-panel]')) {
        // Paneles mobile: directos al <body> (como el teletransporte original).
        document.body.appendChild(node);
        const h = node.dataset.moxMobilePanel;
        this.mobilePanels.set(h, node);
        this._bindMobilePanel(node, h);
      } else if (node.matches('[data-mox-chars-panel]')) {
        document.body.appendChild(node);
        this._bindCharsPanel(node);
      }
    });

    this._updateTopPosition();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Desktop
  // ──────────────────────────────────────────────────────────────────────────

  _bindDesktopTriggers() {
    document.querySelectorAll('[data-mox-nav]').forEach(li => {
      const handle = li.dataset.moxNav;
      if (!handle) return;
      if (!this.templates.has(handle) && !this.panels.has(handle)) return;

      li.addEventListener('pointerenter', () => { this._ensureBuilt(handle); this._show(handle); });
      li.addEventListener('focusin',      () => { this._ensureBuilt(handle); this._show(handle); });
      li.addEventListener('pointerleave', () => this._scheduleHide());
      li.addEventListener('focusout', (e) => {
        if (!li.contains(e.relatedTarget)) this._scheduleHide();
      });
    });
  }

  _bindDesktopPanel(inner, handle) {
    // MOXIE: mantener el header sólido (aria-expanded) mientras el puntero/foco
    // está sobre el panel mismo, no solo sobre el <li> del nav — el panel vive
    // en <body>, fuera del <li>, así que el hover se pierde al cruzar el gap.
    inner.addEventListener('pointerenter', () => {
      this._cancelHide();
      this._setExpanded(handle, true);
    });
    inner.addEventListener('pointerleave', () => this._scheduleHide());
    inner.addEventListener('focusin', () => {
      this._cancelHide();
      this._setExpanded(handle, true);
    });
    inner.addEventListener('focusout', (e) => {
      if (!inner.contains(e.relatedTarget)) this._scheduleHide();
    });
  }

  _getHeaderBottom() {
    // PERF (MOXIE): getBoundingClientRect() fuerza layout. Se cachea por
    // sesión de hover/scroll y solo se invalida en resize/scroll (ver _init).
    if (this._headerRectCache !== null) return this._headerRectCache;

    const header =
      document.getElementById('header-component') ||
      document.querySelector('sticky-header') ||
      document.querySelector('header-component') ||
      document.querySelector('.mox-header') ||
      document.querySelector('header');
    this._headerRectCache = header ? header.getBoundingClientRect().bottom : 0;
    return this._headerRectCache;
  }

  _invalidateHeaderRect() {
    this._headerRectCache = null;
  }

  _updateTopPosition() {
    // Los paneles viven en <body> (clonados o adoptados), así que se expone la
    // variable en <body> para que hereden hacia .mox-mmenu / .mox-mob-panel.
    const top = this._getHeaderBottom();
    // PERF (MOXIE): desacoplar la escritura de estilo de la lectura de layout
    // que la precede (forced-reflow pattern).
    requestAnimationFrame(() => {
      document.body.style.setProperty('--mox-panel-top', `${top}px`);
    });
  }

  _show(handle) {
    this._cancelHide();
    if (this._activeHandle && this._activeHandle !== handle) {
      this._hidePanel(this._activeHandle);
    }
    const inner = this.panels.get(handle);
    if (!inner) return;
    this._updateTopPosition();
    inner.classList.add(ACTIVE_CLASS);
    inner.setAttribute('aria-hidden', 'false');
    this._activeHandle = handle;

    // Marcar el nav item activo para que el CSS pueda aplicar el color de hover
    this._setExpanded(handle, true);
  }

  /**
   * Set aria-expanded on the [data-mox-nav] handle's link. This is the flag
   * Horizon's transparent-header CSS (.header[transparent]:has(...[aria-expanded='true']))
   * reads to keep the header solid — see the MOXIE note in _bindDesktopPanel().
   */
  _setExpanded(handle, expanded) {
    const li = document.querySelector(`[data-mox-nav="${handle}"]`);
    const link = li && li.querySelector('.menu-list__link');
    if (link) link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  _scheduleHide() {
    this._cancelHide();
    this._hideTimer = setTimeout(() => this._hideAll(), HIDE_DELAY);
  }

  _cancelHide() {
    if (this._hideTimer !== null) {
      clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }
  }

  _hidePanel(handle) {
    const inner = this.panels.get(handle);
    if (!inner) return;
    inner.classList.remove(ACTIVE_CLASS);
    inner.setAttribute('aria-hidden', 'true');

    // Quitar el marcado activo del nav item
    this._setExpanded(handle, false);
  }

  _hideAll() {
    this.panels.forEach((_, h) => this._hidePanel(h));
    this._activeHandle = null;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Theme Editor support (desktop)
  // ──────────────────────────────────────────────────────────────────────────

  _bindEditor() {
    document.addEventListener('shopify:section:select', (e) => {
      const inner = e.target.querySelector('[data-mox-menu]');
      if (inner) this._show(inner.dataset.moxMenu);
    });
    document.addEventListener('shopify:section:deselect', () => this._hideAll());
    document.addEventListener('shopify:block:select', (e) => {
      const section = e.target.closest('.mox-mega-menu-section');
      if (!section) return;
      const inner = section.querySelector('[data-mox-menu]');
      if (inner) this._show(inner.dataset.moxMenu);
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      const section = e.target.closest('.mox-mega-menu-section');
      if (section && !section.contains(document.activeElement)) this._hideAll();
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Mobile
  // ──────────────────────────────────────────────────────────────────────────

  _bindMobileTriggers() {
    const handles = new Set([...this.templates.keys(), ...this.mobilePanels.keys()]);
    handles.forEach(handle => {
      // Horizon renders drawer links as: <a id="HeaderDrawer-{handle}" ...>
      const drawerLink = document.getElementById('HeaderDrawer-' + handle);
      if (!drawerLink) return;
      drawerLink.addEventListener('click', (e) => {
        if (window.innerWidth >= 1200) return; // desktop: do nothing
        this._ensureBuilt(handle);
        if (!this.mobilePanels.has(handle)) return; // sin panel mobile → nav normal
        e.preventDefault();
        this._openMobilePanel(handle);
      });
    });
  }

  _bindMobilePanel(panel, handle) {
    // Back button on Level-2 panel → close panel, return to drawer
    panel.querySelectorAll('[data-mox-back]').forEach(btn => {
      btn.addEventListener('click', () => this._closeMobilePanel(handle));
    });

    // Tap on brand row → open Level-3 chars panel
    panel.querySelectorAll('[data-mox-open-chars]').forEach(btn => {
      btn.addEventListener('click', () => {
        this._openCharsPanel(btn.dataset.moxOpenChars);
      });
    });
  }

  _bindCharsPanel(charsPanel) {
    // Back buttons on Level-3 (chars) panels → go back to Level-2
    charsPanel.querySelectorAll('[data-mox-back-chars]').forEach(btn => {
      btn.addEventListener('click', () => {
        charsPanel.classList.remove(MOB_ACTIVE_CLASS);
        charsPanel.setAttribute('aria-hidden', 'true');
      });
    });
  }

  _openMobilePanel(handle) {
    const panel = this.mobilePanels.get(handle);
    if (!panel) return;

    // Close any other open mobile panel first
    this.mobilePanels.forEach((p, h) => {
      if (h !== handle) {
        p.classList.remove(MOB_ACTIVE_CLASS);
        p.setAttribute('aria-hidden', 'true');
      }
    });

    panel.classList.add(MOB_ACTIVE_CLASS);
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mox-mob-panel-open');
  }

  _closeMobilePanel(handle) {
    const panel = this.mobilePanels.get(handle);
    if (!panel) return;

    // Close all Level-3 chars panels (panels were extracted to body, so we
    // can no longer use closest('.mox-mega-menu-section') as a scope)
    document.querySelectorAll('[data-mox-chars-panel]').forEach(cp => {
      cp.classList.remove(MOB_ACTIVE_CLASS);
      cp.setAttribute('aria-hidden', 'true');
    });

    panel.classList.remove(MOB_ACTIVE_CLASS);
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mox-mob-panel-open');
  }

  _openCharsPanel(blockId) {
    const charsPanel = document.querySelector(`[data-mox-chars-panel="${blockId}"]`);
    if (!charsPanel) return;
    charsPanel.classList.add(MOB_ACTIVE_CLASS);
    charsPanel.setAttribute('aria-hidden', 'false');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new MoxMegaMenu());
} else {
  new MoxMegaMenu();
}
